import React, { Fragment, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useAlert } from "react-alert";
import { PaystackConsumer } from "react-paystack";
import { getExamSubscriptions, userSubscribePlan } from "../../actions/subscriptionActions";
import { PLAN_SUBSCRIPTION_RESET } from "../../constants/subscriptionConstants";
import Loader from "../Loader";
import axios from "axios";

const _api_path = process.env.REACT_APP_API_BASE_URL || "/api/v1";

const isPaystackPaymentApproved = (reference) => {
  if (!reference) return false;
  const status = String(reference.status || "").toLowerCase();
  const message = String(reference.message || "").toLowerCase();
  return status === "success" || message === "approved" || message === "success";
};

const getRegistrationContinuePath = () => {
  const examSlug = String(localStorage.getItem("examSlug") || "").toLowerCase();
  const examName = String(localStorage.getItem("examName") || "").toLowerCase();
  const examId = localStorage.getItem("examId");
  const categoryId = localStorage.getItem("categoryId");
  const courseId = localStorage.getItem("courseId");
  const subjects = localStorage.getItem("subjects");
  const pendingPath = localStorage.getItem("pendingSubjectRegistration");

  // Prefer pending destination only when prerequisites are already in place
  if (pendingPath === "/course-subjects" && (courseId || subjects)) {
    return "/course-subjects";
  }

  if (!examId) {
    return "/exams";
  }

  if (examSlug.includes("post-utme") || examName.includes("post-utme") || examName.includes("post utme")) {
    return "/university";
  }

  if (examSlug.includes("bece") || examName.includes("bece") || examName.includes("jsce")) {
    return "/course-subjects";
  }

  // JAMB / WAEC and similar: pick category → course → subjects
  if (!categoryId) {
    return "/categories";
  }

  if (!courseId) {
    return "/category-courses";
  }

  return "/course-subjects";
};

const PlanSubscriptions = ({ history }) => {
  const alert = useAlert();
  const dispatch = useDispatch();

  const [subscriptionType, setSubscriptionType] = useState("");
  const [subscriptionPrice, setSubscriptionPrice] = useState(0);
  const [userSubscription, setUserSubscription] = useState(null);
  const [discountCode, setDiscountCode] = useState("");
  const [discountInfo, setDiscountInfo] = useState(null);
  const [discountError, setDiscountError] = useState("");
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);

  const { user } = useSelector((state) => state.auth) || {};
  const examId = localStorage.getItem("examId");
  const universityIdRaw = localStorage.getItem("universityId");
  const universityId =
    universityIdRaw && String(universityIdRaw).trim() !== "" ? universityIdRaw : null;
  const userId = user?._id || null;
  const [forceRenewal, setForceRenewal] = useState(
    () => localStorage.getItem("forceSubscriptionRenewal") === "true"
  );
  const purchaseGuardRef = useRef(false);
  const redirectedAfterPurchaseRef = useRef(false);

  const continueAfterPurchase = (subscriptionRecord) => {
    if (redirectedAfterPurchaseRef.current) {
      return;
    }
    redirectedAfterPurchaseRef.current = true;

    const subscriptionId = subscriptionRecord?._id || localStorage.getItem("userSubscription");

    if (subscriptionId) {
      localStorage.setItem("userSubscription", subscriptionId);
    }

    // Avoid empty-string universityId breaking later API calls
    const universityIdValue = localStorage.getItem("universityId");
    if (!universityIdValue || String(universityIdValue).trim() === "") {
      localStorage.removeItem("universityId");
    }

    const nextPath = getRegistrationContinuePath();
    localStorage.removeItem("pendingSubjectRegistration");
    localStorage.removeItem("forceSubscriptionRenewal");
    // Never auto-register subjects after payment — the student must choose them.
    localStorage.removeItem("paymentCompleted");
    localStorage.removeItem("subjects");
    localStorage.removeItem("processedSubjects");

    if (nextPath === "/course-subjects") {
      localStorage.setItem("subscriptionJustPurchased", "true");
    } else {
      localStorage.removeItem("subscriptionJustPurchased");
    }

    dispatch({ type: PLAN_SUBSCRIPTION_RESET });
    history.push(nextPath);
  };

  const fetchSubscription = async () => {
    try {
      if (!userId || !examId) {
        return null;
      }

      let response;
      if (universityId) {
        response = await axios.get(
          `${_api_path}/get-user-subscriptions/${userId}/${examId}/${universityId}`
        );
      } else {
        response = await axios.get(`${_api_path}/get-user-subscriptions/${userId}/${examId}`);
      }
      const active = response.data?.userSubscription || null;
      setUserSubscription(active);
      return active;
    } catch (error) {
      console.error("Error fetching user subscription:", error);
      alert.error("Failed to fetch user subscription.");
      return null;
    }
  };

  useEffect(() => {
    const syncForceRenewal = () => {
      setForceRenewal(localStorage.getItem("forceSubscriptionRenewal") === "true");
    };

    syncForceRenewal();
    window.addEventListener("storage", syncForceRenewal);

    return () => {
      window.removeEventListener("storage", syncForceRenewal);
    };
  }, []);

  useEffect(() => {
    if (examId && userId) {
      dispatch(getExamSubscriptions(examId, userId));
      fetchSubscription().then((active) => {
        const pendingPath = localStorage.getItem("pendingSubjectRegistration");
        const storedSubscriptionId = localStorage.getItem("userSubscription");
        const mustRenew = localStorage.getItem("forceSubscriptionRenewal") === "true";

        // Resume subject selection after purchase — never auto-register subjects.
        if (
          pendingPath &&
          storedSubscriptionId &&
          !mustRenew &&
          !redirectedAfterPurchaseRef.current
        ) {
          continueAfterPurchase(active || { _id: storedSubscriptionId });
          return;
        }

        // Already subscribed and not forced to buy another plan — finish registration
        if (active?._id && !mustRenew && !redirectedAfterPurchaseRef.current) {
          localStorage.setItem("userSubscription", active._id);
          localStorage.removeItem("forceSubscriptionRenewal");
          continueAfterPurchase(active);
        }
      });
    }
  }, [dispatch, examId, userId, universityId]);

  const {
    examSubscriptions,
    isPlanSubscribed,
    sloading,
    userSubscription: createdSubscription,
    error: subscriptionError,
  } = useSelector((state) => state.examSubscriptions);

  useEffect(() => {
    if (subscriptionError) {
      alert.error(subscriptionError);
      dispatch({ type: PLAN_SUBSCRIPTION_RESET });
    }
  }, [subscriptionError, alert, dispatch]);

  useEffect(() => {
    if (isPlanSubscribed && createdSubscription) {
      alert.success("Subscription plan subscribed successfully.");
      continueAfterPurchase(createdSubscription);
    }
  }, [isPlanSubscribed, createdSubscription, alert]);

  const setSubscriptionTypeHandle = (e) => {
    const price = Number(e.target.dataset.price || 0);
    setSubscriptionType(e.target.value);
    setSubscriptionPrice(price);
    setDiscountCode("");
    setDiscountInfo(null);
    setDiscountError("");
  };

  const formatCurrency = (amount) => `₦ ${Number(amount || 0).toLocaleString()}`;

  const handleDiscountInputChange = (value) => {
    setDiscountCode(value);
    if (!value.trim()) {
      setDiscountInfo(null);
      setDiscountError("");
    }
  };

  const originalAmount = Number(subscriptionPrice) || 0;
  const finalPayableAmount = Number(discountInfo?.finalAmount ?? originalAmount);
  const discountAmount = Number(discountInfo?.discountAmount ?? 0);

  const validateSubscription = () => {
    if (!examId) {
      alert.error("Exam context is missing. Please select an exam again.");
      history.push("/exams");
      return false;
    }

    if (!subscriptionType) {
      alert.error("Please choose a subscription plan.");
      return false;
    }

    const existingPrice = Number(userSubscription?.price ?? 0);
    const selectedPrice = Number(subscriptionPrice ?? 0);
    const renewalOverride = forceRenewal || !userSubscription;

    if (!renewalOverride && existingPrice > 0 && selectedPrice > 0 && existingPrice > selectedPrice) {
      alert.error("You have an existing higher subscription plan.");
      return false;
    }

    return true;
  };

  const buildPaymentData = (overrides = {}) => {
    const payload = {
      currency: "NGR",
      paymentMethod: "Paystack",
      paymentMessage: "Successful",
      paymentStatus: "success",
      originalAmount,
      finalAmount: finalPayableAmount,
      ...overrides,
    };

    if (discountInfo?.discount?.code) {
      payload.discount = {
        code: discountInfo.discount.code,
        amount: discountAmount,
        finalAmount: finalPayableAmount,
        originalAmount,
      };
    }

    return payload;
  };

  const finalizeDiscountUsage = async (transactionId) => {
    if (!discountInfo?.discount?.code) return;
    try {
      await axios.post(`${_api_path}/apply`, {
        code: discountInfo.discount.code,
        planId: subscriptionType,
        amount: Number(discountInfo.originalAmount ?? originalAmount),
        transactionId,
      });
    } catch (error) {
      console.error("Failed to record discount usage:", error);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!userId) {
      alert.error("Please log in to continue.");
      return;
    }

    if (!validateSubscription()) {
      return;
    }

    if (purchaseGuardRef.current) {
      return;
    }

    purchaseGuardRef.current = true;
    const refId = Math.floor(Math.random() * 1000000).toString();
    const paymentData = buildPaymentData({
      refrenceId: refId,
      paymentTransaction: Math.floor(Math.random() * 100000),
      trxref: refId,
    });

    try {
      const result = await dispatch(
        userSubscribePlan(examId, universityId, subscriptionType, userId, paymentData)
      );
      await finalizeDiscountUsage(refId);
      if (result?.primarySubscription) {
        continueAfterPurchase(result.primarySubscription);
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.errMessage ||
        error?.message ||
        "Unable to activate subscription.";
      alert.error(message);
    } finally {
      purchaseGuardRef.current = false;
    }
  };

  const handleSuccess = (reference) => {
    if (!userId) {
      alert.error("Unable to complete payment because your session expired. Please log in again.");
      return;
    }

    if (!isPaystackPaymentApproved(reference)) {
      alert.error(
        "Payment couldn't be completed (" +
          (reference?.message || reference?.status || "unknown") +
          ")."
      );
      return;
    }

    if (purchaseGuardRef.current) {
      return;
    }

    purchaseGuardRef.current = true;
    const paymentData = buildPaymentData({
      paymentMessage: reference.message || "Approved",
      refrenceId: reference.reference,
      paymentTransaction: reference.transaction || reference.trans,
      trxref: reference.trxref || reference.reference,
      paymentStatus: reference.status || "success",
    });

    Promise.resolve(
      dispatch(userSubscribePlan(examId, universityId, subscriptionType, userId, paymentData))
    )
      .then((result) => {
        const transactionId = reference.reference || reference.trxref || reference.transaction;
        return finalizeDiscountUsage(transactionId).then(() => result);
      })
      .then((result) => {
        if (result?.primarySubscription) {
          alert.success("Subscription plan subscribed successfully.");
          continueAfterPurchase(result.primarySubscription);
        }
      })
      .catch((error) => {
        console.error("Failed to process subscription after payment:", error);
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.errMessage ||
          "Payment succeeded, but subscription update failed. Please contact support.";
        alert.error(message);
      })
      .finally(() => {
        purchaseGuardRef.current = false;
      });
  };

  const handleClose = () => {
    alert.error("You have cancelled the payment.");
  };

  const handlePaystack = (initializePayment) => {
    if (!userId) {
      alert.error("Please log in to continue.");
      return;
    }

    if (!validateSubscription()) {
      return;
    }

    if (typeof initializePayment !== "function") {
      alert.error("Payment service is not ready. Please try again in a moment.");
      return;
    }

    try {
      initializePayment((reference) => {
        handleSuccess(reference);
      }, handleClose);
    } catch (error) {
      console.error("Failed to initialize Paystack:", error);
      alert.error("Unable to open payment gateway. Please try again.");
    }
  };

  const getPaystackConfig = () => {
    const amountValue = Number(finalPayableAmount || 0);
    const priceInKobo = Math.round(amountValue * 100);
    const paystackKey =
      process.env.REACT_APP_PAYSTACK_PUBLIC_KEY ||
      "pk_test_c5f316c291b070ad3b3873412bbf76fc6bb57e29";
    return {
      reference: new Date().getTime().toString(),
      email: user?.email || "",
      amount: priceInKobo,
      publicKey: paystackKey,
      text: "Paystack Button Implementation",
      onSuccess: handleSuccess,
      onClose: handleClose,
    };
  };

  const applyDiscountCode = async () => {
    if (!subscriptionType) {
      alert.error("Please choose a subscription plan before applying a discount code.");
      return;
    }

    const code = discountCode.trim().toUpperCase();
    if (!code) {
      setDiscountError("Enter a discount code to continue.");
      return;
    }

    setIsApplyingDiscount(true);
    setDiscountError("");

    try {
      setDiscountCode(code);
      const { data } = await axios.post(`${_api_path}/validate`, {
        code,
        planId: subscriptionType,
        amount: originalAmount,
      });

      if (data?.success && data?.data) {
        setDiscountInfo(data.data);
        setDiscountError("");
        alert.success(data.message || "Discount applied successfully.");
      } else {
        setDiscountInfo(null);
        const message = data?.message || "Unable to apply discount.";
        setDiscountError(message);
        alert.error(message);
      }
    } catch (error) {
      console.error("Discount validation failed:", error);
      const message = error?.response?.data?.message || "Failed to apply discount.";
      setDiscountInfo(null);
      setDiscountError(message);
      alert.error(message);
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  return (
    <Fragment>
      <section
        className="page-banner-sec text-center"
        style={{
          backgroundImage: `url("../assets/frontend/img/about-banner.jpg")`,
        }}
      >
        <div className="container">
          <ul className="clearfix paginations-cust">
            <li>
              <a href="#!">Home</a>
            </li>
            <li>
              <a href="#!">Subscription</a>
            </li>
          </ul>
          <h1>Subscription Plan</h1>
        </div>
      </section>

      <section className="subscription-page">
        <div className="container">
          {!examId ? (
            <div className="end-plan" style={{ marginBottom: 24 }}>
              <h1>Exam not selected</h1>
              <p>Please choose an exam first, then return to pick a subscription plan.</p>
              <Link to="/exams" className="chose-more">
                Go to Exams
              </Link>
            </div>
          ) : null}
          <div className="custom-radios-plan">
            {sloading ? (
              <Loader />
            ) : (
              <div className="wrapper clearfix">
                {examSubscriptions && examSubscriptions.length > 0 ? (
                  examSubscriptions.map((item, key) => {
                    const indexId = key + 1;
                    return (
                      <Fragment key={item._id || indexId}>
                        <input
                          type="radio"
                          name="select"
                          id={`option-${indexId}`}
                          value={item._id}
                          data-price={item.price}
                          checked={subscriptionType === item._id}
                          onChange={setSubscriptionTypeHandle}
                        />
                        <label
                          htmlFor={`option-${indexId}`}
                          className={`option option-${indexId}${
                            subscriptionType === item._id ? " checked" : ""
                          }`}
                        >
                          <div className="dot"></div>
                          <span>
                            {item.title} {item.subscriptionType} <b>{item.description}</b>
                          </span>
                          <div className="chose-more">₦ {item.price}</div>
                        </label>
                      </Fragment>
                    );
                  })
                ) : (
                  <p>
                    {examId
                      ? "No subscription plans available for this exam."
                      : "Select an exam to view subscription plans."}
                  </p>
                )}
              </div>
            )}
          </div>
          {subscriptionType ? (
            <div className="discount-wrapper">
              <h3 className="discount-title">Have a discount code?</h3>
              <div className="discount-input-group">
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => handleDiscountInputChange(e.target.value)}
                  placeholder="Enter discount code"
                  className="discount-input"
                />
                <button
                  type="button"
                  className="chose-more apply-discount"
                  onClick={applyDiscountCode}
                  disabled={isApplyingDiscount}
                >
                  {isApplyingDiscount ? "Applying..." : "Apply"}
                </button>
              </div>
              {discountError ? <p className="discount-message error">{discountError}</p> : null}
              {discountInfo ? (
                <div className="discount-summary">
                  <p>
                    Original Amount: <strong>{formatCurrency(discountInfo.originalAmount)}</strong>
                  </p>
                  <p>
                    Discount: <strong>-{formatCurrency(discountAmount)}</strong>
                  </p>
                  <p>
                    Payable Now: <strong>{formatCurrency(finalPayableAmount)}</strong>
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="next-prev-steps text-center">
            <Link to="/my-subscriptions" className="chose-more">
              <i className="fas fa-arrow-left"></i> Prev
            </Link>
            {!subscriptionType ? (
              <button type="button" className="chose-more" onClick={validateSubscription}>
                Next <i className="fas fa-arrow-right"></i>
              </button>
            ) : Number(finalPayableAmount) <= 0 ? (
              <button type="button" className="chose-more" onClick={submitHandler}>
                Next <i className="fas fa-arrow-right"></i>
              </button>
            ) : (
              <PaystackConsumer {...getPaystackConfig()}>
                {({ initializePayment }) => (
                  <button
                    type="button"
                    className="chose-more"
                    onClick={() => handlePaystack(initializePayment)}
                  >
                    Pay {formatCurrency(finalPayableAmount)} <i className="fas fa-arrow-right"></i>
                  </button>
                )}
              </PaystackConsumer>
            )}
          </div>
        </div>
      </section>
    </Fragment>
  );
};

export default PlanSubscriptions;
